import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { fmtInt, fmtPct } from "../../../utils/format";

/** Una columna: lo conseguido (o previsto) frente al ideal, en acumulado. */
export interface GoalColumn {
  label: string;
  /** Valor de la barra. */
  value: number;
  /** Ideal acumulado ese día: la línea discontinua. */
  ideal: number;
  /** true: es predicción, la barra va en gris. */
  forecast: boolean;
}

/** Diferencia frente al ideal, en %, con signo. null sin ideal. */
export const diffPercent = (value: number, ideal: number): number | null =>
  ideal > 0 ? ((value - ideal) / ideal) * 100 : null;

const signedPct = (n: number) =>
  `${n > 0 ? "+" : ""}${fmtPct(n, Math.abs(n) < 10 ? 1 : 0)}`;

const COLUMN_WIDTH = 88;

/**
 * Columnas a la derecha de la gráfica de objetivo: ayer (lo conseguido) y
 * final de vuelo (la predicción), cada una con su ideal en discontinua y la
 * diferencia en %. Las dos comparten escala para poder compararlas.
 */
const WipGoalColumns: React.FC<{
  columns: GoalColumn[];
  color: string;
  height: number;
}> = ({ columns, color, height }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const max = Math.max(...columns.flatMap((c) => [c.value, c.ideal]), 0) * 1.08;
  const pct = (n: number) => (max > 0 ? `${(n / max) * 100}%` : "0%");
  const forecastColor = theme.palette.grey[500];

  return (
    <Stack direction="row" spacing={1.5} sx={{ height, flexShrink: 0 }}>
      {columns.map((c) => {
        const diff = diffPercent(c.value, c.ideal);
        const diffColor =
          diff == null
            ? theme.palette.text.secondary
            : diff >= 0
              ? theme.palette.success.main
              : theme.palette.error.main;
        const barColor = c.forecast ? forecastColor : color;
        return (
          <Stack
            key={c.label}
            alignItems="center"
            spacing={0.5}
            sx={{ width: COLUMN_WIDTH }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {c.label}
            </Typography>
            <Typography
              sx={{ fontSize: 18, fontWeight: 700, color: diffColor }}
              title={t("wip.charts.goalColumnsDiff")}
            >
              {diff == null ? "—" : signedPct(diff)}
            </Typography>
            <Box
              sx={{
                position: "relative",
                flex: 1,
                width: "100%",
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: "22%",
                  right: "22%",
                  height: pct(c.value),
                  bgcolor: barColor,
                  opacity: c.forecast ? 0.55 : 0.9,
                  borderRadius: "4px 4px 0 0",
                }}
              />
              {c.ideal > 0 && (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    left: "8%",
                    right: "8%",
                    bottom: pct(c.ideal),
                    borderTop: `2px dashed ${color}`,
                    opacity: 0.7,
                  }}
                />
              )}
            </Box>
            <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
              <Typography
                variant="caption"
                component="div"
                color="text.primary"
                sx={{ fontWeight: 600 }}
              >
                {fmtInt(Math.round(c.value))}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
              >
                {t("wip.charts.goalColumnsIdeal", {
                  value: fmtInt(Math.round(c.ideal)),
                })}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default WipGoalColumns;
